import { makeAutoObservable } from 'mobx';
// import { RootStore } from 'mobx-stores/root-store'
import { StatisticsHighlights, StatisticsStores, StatisticsTableEntryDTO } from 'models/statistics';
import { Api, User } from 'services';
import moment from 'moment';
import { PeriodValue } from '@touche-lts/panel-ui/dist/components/shared/date-range-picker';
import { StoreDTO } from 'models/store';
import { normalizeStatisticsListResponse } from 'mobx-stores/utils/statistics-table-base/statistics-table-base.utils';

export class StatisticsStore {
  private rootStore: any
  private storeApi: Api['storeApi']
  private statisticsApi: Api['statisticsApi']

  constructor(rootStore: any) {
    this.rootStore = rootStore
    this.storeApi = this.rootStore.generalStore.api.storeApi
    this.statisticsApi = this.rootStore.generalStore.api.statisticsApi
    makeAutoObservable(this);
  }

  // OBSERVABLES
  private highLights: StatisticsHighlights = null
  private graphInfo: StatisticsTableEntryDTO[] = null
  private stores: StoreDTO[] = null
  private todayPeriod: string = moment().toISOString();
  private detaulfPeriod: string = moment().subtract(7, 'd').toDate().toISOString();
  public endPeriod: string = moment().toISOString();
  public periodDate: string = moment().subtract(7, 'd').toDate().toISOString();

  // ACTIONS
  public setHighlights = (highLights: StatisticsHighlights): void => {
    this.highLights = highLights
  }

  public setGraphInfo = (graphInfo: StatisticsTableEntryDTO[]): void => {
    this.graphInfo = graphInfo
  }

  public setPeriodDate = (dateISOString: string): void => {
    this.resetPeriods();
    this.periodDate = dateISOString;
  }

  /**
   * Custom range from PeriodPicker: keep graph + highlights in sync with table (same dates + store).
   * When storeId is omitted or NaN (e.g. from Number('All stores')), use current table selection.
   */
  public setCustomPeriod = (period: PeriodValue, storeId?: number | string) => {
    if (period) {
      this.periodDate = period.start.toISOString();
      this.endPeriod = period.end.toISOString();
    } else {
      this.resetPeriods();
    }
    const resolvedStore =
      storeId !== undefined &&
      storeId !== null &&
      !(typeof storeId === 'number' && Number.isNaN(storeId))
        ? storeId
        : this.rootStore?.statisticsTableStore?.storeSelected
    void this.getGraphInfo(resolvedStore)
    void this.getHighLights(resolvedStore)
  }

  // COMPUTED
  public get statisticsHighlights(): StatisticsHighlights {
    return this.highLights
  }

  public get statisticsGraph(): StatisticsTableEntryDTO[] {
    return this.graphInfo
  }

  public get storesList(): StoreDTO[] {
    return this.stores
  }

  public get statiSticsGraphData() {
    const labels = this.graphInfo ? this.statisticsGraph.map(graphInfo => moment(graphInfo.date).format('DD.MM.YY')) : []
    return {
      labels,
      datasets: [
        {
          type: 'line' as const,
          label: 'Products sold',
          borderColor: '#B021D9',
          borderWidth: 2,
          fill: false,
          data: this.graphInfo ? this.statisticsGraph.map(graphInfo => graphInfo.sold) : [],
        },
        {
          type: 'bar' as const,
          label: 'Revenue',
          backgroundColor: '#3AABF2',
          data: this.graphInfo ? this.statisticsGraph.map(graphInfo => graphInfo.revenueToday) : [],
          borderColor: 'white',
          borderWidth: 2,
        }
      ],
    }
  }

  /** Resolves store id list for filters; never throws when stores are not loaded yet. */
  private getStoreIdsForFilter = (storeId?: number | string): number[] | undefined => {
    if (storeId == null || storeId === '') return undefined
    if (storeId === StatisticsStores.ALLSTORES) {
      if (!this.stores?.length) return undefined
      return this.stores.map(store => store.id)
    }
    const n = Number(storeId)
    if (Number.isNaN(n)) return undefined
    return [n]
  }

  // METHODS
  public getHighLights = async (storeId?: number | string): Promise<void> => {
    if (User.isStoreAdmin) {
      await this.getStoreAdminHighLights(storeId);
      return;
    }

    let storeIds;
    if (storeId) {
      if (storeId === StatisticsStores.ALLSTORES) {
        storeIds = [...this.stores.map(store => store.id)];
      } else {
        storeIds = [Number(storeId)];
      }
    }
    const filterPayload = {
      dateStart: this.periodDate,
      dateEnd: this.endPeriod,
      storeIds,
    }
    const statHighlights = await this.statisticsApi.getHighLights(filterPayload);
    if (!statHighlights) return;
    this.setHighlights(statHighlights);
  }

  private getStoreAdminHighLights = async (storeId?: number | string): Promise<void> => {
    const targetStoreId = storeId && storeId !== StatisticsStores.ALLSTORES
      ? Number(storeId)
      : this.stores?.[0]?.id;

    if (!targetStoreId) return;

    const filterPayload = {
      dateStart: this.periodDate,
      dateEnd: this.endPeriod,
    }
    const dto = await this.statisticsApi.getStoreAdminHighlights(targetStoreId, filterPayload);
    if (!dto) return;

    // Map StoreAdminHighlightsDTO fields to StatisticsHighlights shape
    this.setHighlights({
      totalRevenue: dto.totalRevenue,
      productsSold: dto.productsSold,
      orderCount: dto.totalOrders,
      activeProducts: dto.totalProducts,
      visitingUsers: dto.totalVisitors,
      bestSellingProduct: dto.bestSellingProduct,
    } as StatisticsHighlights);
  }

  public getGraphInfo = async (storeId?: number | string): Promise<StatisticsTableEntryDTO[]> => {
    const storeIds = this.getStoreIdsForFilter(storeId)
    const filterPayload = {
      dateEnd: this.endPeriod,
      dateStart: this.periodDate,
      storeIds: storeIds
    }
    const statGraph = await this.statisticsApi.getStoreStatisticsGraph(filterPayload);
    if (statGraph == null) {
      this.setGraphInfo([])
      return
    }
    const { items } = normalizeStatisticsListResponse<StatisticsTableEntryDTO>(statGraph)
    this.setGraphInfo(items)
  }

  public getStores = async (storeId?: number): Promise<StoreDTO[]> => {
    if (storeId) {
      this.getGraphInfo(storeId);
      this.getHighLights(storeId);
    }

    const stores = User.isStoreAdmin
      ? await this.storeApi.getCurrentUserStores()
      : await this.storeApi.getStores();

    if (!stores) return;
    this.stores = stores;

    if (
      storeId == null &&
      this.rootStore?.statisticsTableStore?.storeSelected === StatisticsStores.ALLSTORES
    ) {
      await this.getGraphInfo(StatisticsStores.ALLSTORES)
      await this.getHighLights(StatisticsStores.ALLSTORES)
    }
  }

  private resetPeriods = (): void => {
    this.periodDate = JSON.parse(JSON.stringify(this.detaulfPeriod));
    this.endPeriod = JSON.parse(JSON.stringify(this.todayPeriod));
  }

}
